import { EditableText } from '@/components/EditableText.tsx';
import useUpdateAutomation from '@/hooks/api/automations/useUpdateAutomation.ts';
import { Accordion, Box, Divider, Flex, Stack, Text } from '@mantine/core';
import React, { useCallback, useState } from 'react';
import { DataSourceForm } from '@/pages/Editor/components/StepSidebar/DataSources/DataSourceForm.tsx';
import { Automation } from '@/types/automations.ts';
import {
  AUTOMATION_DESCRIPTION_MAX_LENGTH,
  AUTOMATION_NAME_MAX_LENGTH,
} from '@/utils/constants.ts';
import AutomationModelSettingsPanel from '@/pages/Editor/components/StepSidebar/AutomationModelSettingsPanel.tsx';
import AutomationNotificationSettingsPanel from '@/pages/Editor/components/StepSidebar/AutomationNotificationSettingsPanel.tsx';
import BrowserNotificationSettingsPanel from '@/pages/Editor/components/StepSidebar/BrowserNotificationSettingsPanel.tsx';
import { AutomationModelSettings } from '@/types/llmModels.ts';
import { AutomationJsonSchema } from '@/types/automationFields.ts';
import AutomationSchemasPanel from '@/pages/Editor/components/StepSidebar/AutomationSchemasPanel.tsx';
import { AutomationPortalSettingsPanel } from '@/pages/Editor/components/StepSidebar/AutomationPortalSettingsPanel.tsx';
import AutomationEmailIdentityPanel from '@/pages/Editor/components/StepSidebar/AutomationEmailIdentityPanel.tsx';
import AutomationRetirementPanel from '@/pages/Editor/components/StepSidebar/AutomationRetirementPanel.tsx';
import AutomationOutcomeAssessmentPanel from '@/pages/Editor/components/StepSidebar/AutomationOutcomeAssessmentPanel.tsx';

type props = {
  automation: Automation;
  modelSettings: AutomationModelSettings;
  schemas: AutomationJsonSchema[];
};

const MODEL_SELECTION_SECTION = 'model-selection';
const PORTAL_SECTION = 'portal';
const EMAIL_NOTIFICATIONS_SECTION = 'email-notifications';
const BROWSER_NOTIFICATIONS_SECTION = 'browser-notifications';
const EMAIL_IDENTITY_SECTION = 'email-identity';
const DEFINITIONS_SECTION = 'definitions';
const RETIREMENT_SECTION = 'retirement';

const DataSourceSidebar = ({
  automation,
  modelSettings,
  schemas,
}: props) => {
  const [openedConfigSection, setOpenedConfigSection] = useState<string | null>(null);
  const { mutate: updateName, isPending: isUpdatingName } = useUpdateAutomation();

  const updateAutomationName = useCallback(
    (name: string) => updateName({ id: automation.id, name }),
    [automation.id, updateName],
  );

  const { mutate: updateDescription, isPending: isUpdatingDescription } = useUpdateAutomation();

  const updateAutomationDescription = useCallback(
    (description: string) => updateDescription({ id: automation.id, description }),
    [automation.id, updateDescription],
  );

  return (
    <Stack w={'100%'} py={'md'}>
      <Stack gap={0}>
        <Stack gap={'6px'} px={'1px'}>
          <EditableText
            name={automation.name}
            onSave={updateAutomationName}
            saving={isUpdatingName}
            size={'md'}
            placeholder={'Name'}
            textInputHeight={'30px'}
            maxLength={AUTOMATION_NAME_MAX_LENGTH}
          />
          <Flex
            bg={'#F4F4F4'}
            style={{ borderRadius: '3px', padding: '4px 8px' }}
            ml={'16px'}
            w={'max-content'}
          >
            <Text size={'12px'} fw={400} c={'#757575'}>
              {`${automation.run_count} run` + (automation.run_count !== 1 ? 's' : '')}
            </Text>
          </Flex>
        </Stack>

        <Divider style={{ borderColor: '#BABABA' }} ml={'19px'} w={'11px'} mt={'sm'} />
        <Flex px={'4px'} my="2px">
          <EditableText
            name={automation.description}
            onSave={updateAutomationDescription}
            saving={isUpdatingDescription}
            size={'sm'}
            inputType={'textarea'}
            lineClamp={10}
            placeholder={'Add a description'}
            maxLength={AUTOMATION_DESCRIPTION_MAX_LENGTH}
            fw={400}
          />
        </Flex>
        <Divider style={{ borderColor: '#EAEAEA' }} mt={'10px'} />
      </Stack>
      <Box px={'md'}>
        <DataSourceForm />
      </Box>
      <Divider style={{ borderColor: '#EAEAEA' }} />
      <Box px={'md'} pb={'md'}>
        <Accordion
          value={openedConfigSection}
          onChange={setOpenedConfigSection}
          variant="contained"
        >
          <Accordion.Item value={MODEL_SELECTION_SECTION}>
            <Accordion.Control>Model selection</Accordion.Control>
            <Accordion.Panel>
              <AutomationModelSettingsPanel
                automationId={automation.id}
                modelSettings={modelSettings}
                enabled={openedConfigSection === MODEL_SELECTION_SECTION}
              />
            </Accordion.Panel>
          </Accordion.Item>
          <Accordion.Item value={DEFINITIONS_SECTION}>
            <Accordion.Control>Data definitions</Accordion.Control>
            <Accordion.Panel>
              <Accordion variant="contained">
                <Accordion.Item value="schemas">
                  <Accordion.Control>Data schemas</Accordion.Control>
                  <Accordion.Panel>
                    <AutomationSchemasPanel schemas={schemas} />
                  </Accordion.Panel>
                </Accordion.Item>
              </Accordion>
            </Accordion.Panel>
          </Accordion.Item>
          <Accordion.Item value={PORTAL_SECTION}>
            <Accordion.Control>Portal</Accordion.Control>
            <Accordion.Panel>
              <AutomationPortalSettingsPanel
                automation={automation}
                shouldLoad={openedConfigSection === PORTAL_SECTION}
              />
            </Accordion.Panel>
          </Accordion.Item>
          <Accordion.Item value={RETIREMENT_SECTION}>
            <Accordion.Control>Lifecycle</Accordion.Control>
            <Accordion.Panel>
              {openedConfigSection === RETIREMENT_SECTION && (
                <AutomationRetirementPanel automation={automation} shouldLoad />
              )}
            </Accordion.Panel>
          </Accordion.Item>
          <Accordion.Item value="outcome-assessment">
            <Accordion.Control>Outcome assessment</Accordion.Control>
            <Accordion.Panel>
              <AutomationOutcomeAssessmentPanel />
            </Accordion.Panel>
          </Accordion.Item>
          <Accordion.Item value={EMAIL_NOTIFICATIONS_SECTION}>
            <Accordion.Control>Email notifications</Accordion.Control>
            <Accordion.Panel>
              <AutomationNotificationSettingsPanel automation={automation} />
            </Accordion.Panel>
          </Accordion.Item>
          <Accordion.Item value={EMAIL_IDENTITY_SECTION}>
            <Accordion.Control>Automation email identity</Accordion.Control>
            <Accordion.Panel>
              {openedConfigSection === EMAIL_IDENTITY_SECTION && <AutomationEmailIdentityPanel />}
            </Accordion.Panel>
          </Accordion.Item>
          <Accordion.Item value={BROWSER_NOTIFICATIONS_SECTION}>
            <Accordion.Control>Browser notifications</Accordion.Control>
            <Accordion.Panel>
              <BrowserNotificationSettingsPanel />
            </Accordion.Panel>
          </Accordion.Item>
        </Accordion>
      </Box>
    </Stack>
  );
};

export default DataSourceSidebar;
